import { getSoleTokenName } from "@/shared/utils";
import type { AppDispatch, RootState } from "@store";
import { editEntity, setVersionDataValue } from "@store/story";
import { getEntitySettings, getVersionData } from "@store/story/selectors";

export interface SetBoundOrFieldValueArgs {
  entityId: string;
  /** Settings field that holds the resolved/fallback value (e.g. "src"). */
  key: string;
  /**
   * Optional settings field that holds the {{var}} binding template
   * (e.g. "dynamicVariableSrc"). When set and currently a sole-token,
   * the new value is routed to version.data[var] and the value field
   * is left untouched.
   */
  bindingKey?: string;
  value: unknown;
  /** Extra entity data dispatched alongside (e.g. metadata). */
  extra?: Record<string, unknown>;
  /** Skip undo/redo history for the underlying editEntity dispatch. */
  skipHistory?: boolean;
}

export const setBoundOrFieldValue =
  ({
    entityId,
    key,
    bindingKey,
    value,
    extra,
    skipHistory,
  }: SetBoundOrFieldValueArgs) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const settings = getEntitySettings(entityId)(getState()) as
      | Record<string, unknown>
      | undefined;
    const binding =
      bindingKey != null
        ? (settings?.[bindingKey] as string | undefined)
        : undefined;
    const boundVar = getSoleTokenName(binding);

    // Route the value to version.data ONLY when a personalized version is
    // actively being edited. In the base editor there is no version, so
    // setVersionDataValue is a no-op and the change would be silently dropped
    // (e.g. replacing an image that has a variable bound) — fall through to
    // updating the settings field (the resolved/fallback value) instead.
    if (boundVar && getVersionData(getState())) {
      dispatch(setVersionDataValue({ key: boundVar, value }));
      if (extra)
        dispatch(editEntity({ id: entityId, data: extra, skipHistory }));
      return;
    }

    dispatch(
      editEntity({
        id: entityId,
        data: { [key]: value, ...(extra ?? {}) },
        skipHistory,
      }),
    );
  };
